import { useMemo, useState } from 'react';
import { ActionDatePicker } from '@library/components/datePicker/ActionDatePicker';
import type { ConfirmationModalProps } from '@library/components/modals/ConfirmationModal';
import { DatePickerButton } from '@library/components/buttons';
import { DatePickerCustomInput } from '@library/components/datePicker';
import { DateTime } from 'luxon';
import type { GamePlayerProjectionsRequest } from '@library/types/projection';
import { classNames } from '@library/utils';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import type { useUpdateBulkProjectionsMutation } from '@bo-services/projectionsApi';

export const BulkBoardTimeButton = ({
  bulkUpdateProjections,
  setConfirmationModalProps,
  tradingQueryParams
}: {
  bulkUpdateProjections: ReturnType<typeof useUpdateBulkProjectionsMutation>[0];
  setConfirmationModalProps: (
    props: Omit<ConfirmationModalProps, 'onCancelModal'> | false
  ) => void;
  tradingQueryParams: GamePlayerProjectionsRequest;
}) => {
  const {
    durationId,
    fromDate,
    gameId,
    leagueId,
    search,
    statTypeIds,
    status,
    team,
    toDate
  } = tradingQueryParams;

  const [boardTimeOpen, setBoardTimeOpen] = useState(false);

  const handleConfirmBulkUpdateStatus = useMemo(
    () => async (newBulkBoardTime: DateTime) => {
      await bulkUpdateProjections({
        league_ids: leagueId ?? [],
        from_date: formatWithTimezoneChange(DateTime.fromISO(fromDate), 'D'),
        to_date:
          toDate && formatWithTimezoneChange(DateTime.fromISO(toDate), 'D'),
        search: search ?? '',
        game_ids: gameId ? [gameId] : [],
        teams: team ? [team] : [],
        stat_type_ids: statTypeIds ?? [],
        statuses: status ?? [],
        duration_ids: durationId ?? [],
        position_ids: [],
        new_player_ids: [],
        ignore_update_limit: false,
        projection: {
          board_time: formatWithTimezoneChange(newBulkBoardTime, 'LL/dd/yyyy t')
        }
      });
    },
    [
      bulkUpdateProjections,
      leagueId,
      fromDate,
      toDate,
      search,
      gameId,
      team,
      statTypeIds,
      status,
      durationId
    ]
  );

  const handleOnSaveBulkBoardTime = (newBulkBoardTime: DateTime) => {
    setConfirmationModalProps({
      confirmationCopy: 'Change',
      handleConfirm: () => handleConfirmBulkUpdateStatus(newBulkBoardTime),
      label: 'Bulk Changing Board Time',
      children: `Are you sure you want to bulk change the board time for all projections that match your seach criteria to ${newBulkBoardTime.toLocaleString(
        DateTime.DATETIME_FULL
      )}?`,
      isOpen: true
    });
  };

  return (
    <div className="flex flex-col">
      <ActionDatePicker
        changeTime={handleOnSaveBulkBoardTime}
        customInput={
          <DatePickerCustomInput
            className={classNames(
              boardTimeOpen ? 'bg-soFresh-120' : 'bg-soFresh-110',
              'hover:bg-soFresh-120 border-soFresh-140 text-soClean-140 grid h-10 w-full rounded-md border text-xs'
            )}
          >
            <DatePickerButton className="flex h-10 whitespace-nowrap px-3">
              <span className="self-center pb-0.5">Bulk Board Time</span>
            </DatePickerButton>
          </DatePickerCustomInput>
        }
        initialTime={DateTime.now()}
        setTimeOpen={setBoardTimeOpen}
        timeOpen={boardTimeOpen}
      />
    </div>
  );
};
